import React, { useState, useEffect, useCallback } from "react";
import { Tag, Loader2, Printer, Search, Filter, CheckSquare, Square, Package, Truck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { printMultipleLabels, printShipmentLabel } from "@/components/customer/ShipmentLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` } });

const STATUSES = [
  { value: "", label: "Tutti gli stati" },
  { value: "in_attesa", label: "In Attesa" },
  { value: "accettato", label: "Accettato" },
  { value: "in_transito", label: "In Transito" },
  { value: "consegnato", label: "Consegnato" },
];

export default function AdminLabels() {
  const [shipments, setShipments] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ type: "all", status: "", client: "", search: "", dateFrom: "", dateTo: "" });

  const load = useCallback(async () => {
    try {
      const [sR, pR, cR] = await Promise.all([
        axios.get(`${API}/admin/shipments`, authH()),
        axios.get(`${API}/admin/pickups`, authH()),
        axios.get(`${API}/admin/clients`, authH()),
      ]);
      setShipments(sR.data);
      setPickups(pR.data);
      setClients(cR.data);
    } catch { toast.error("Errore caricamento dati"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const allItems = [
    ...shipments.map(s => ({ ...s, _type: "spedizione" })),
    ...pickups.map(p => ({ ...p, _type: "ritiro" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const filtered = allItems.filter(item => {
    if (filters.type === "spedizioni" && item._type !== "spedizione") return false;
    if (filters.type === "ritiri" && item._type !== "ritiro") return false;
    if (filters.status && item.status !== filters.status) return false;
    if (filters.client && item.client_email !== filters.client) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const match = [item.tracking_number, item.client_name, item.company, item.origin, item.destination]
        .filter(Boolean).some(v => v.toLowerCase().includes(q));
      if (!match) return false;
    }
    if (filters.dateFrom) {
      const d = (item.created_at || "").slice(0, 10);
      if (d < filters.dateFrom) return false;
    }
    if (filters.dateTo) {
      const d = (item.created_at || "").slice(0, 10);
      if (d > filters.dateTo) return false;
    }
    return true;
  });

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const selectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.id)));
    }
  };

  const printSelected = () => {
    const items = allItems.filter(i => selected.has(i.id));
    if (!items.length) return toast.error("Seleziona almeno una spedizione/ritiro");
    printMultipleLabels(items, { useServer: true });
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const parts = d.slice(0, 10).split("-");
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d.slice(0, 10);
  };

  const totalLabels = filtered.reduce((sum, item) => {
    const colli = item.colli && item.colli.length > 0 ? item.colli : null;
    if (colli) return sum + colli.reduce((s, c) => s + (parseInt(c.quantita) || 1), 0);
    return sum + 1;
  }, 0);

  const selectedLabels = allItems.filter(i => selected.has(i.id)).reduce((sum, item) => {
    const colli = item.colli && item.colli.length > 0 ? item.colli : null;
    if (colli) return sum + colli.reduce((s, c) => s + (parseInt(c.quantita) || 1), 0);
    return sum + 1;
  }, 0);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#FF5200]" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="admin-labels">
      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-zinc-400"><Filter size={16} /></div>

          <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} data-testid="filter-type"
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white">
            <option value="all">Tutti</option>
            <option value="spedizioni">Spedizioni</option>
            <option value="ritiri">Ritiri</option>
          </select>

          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status"
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white">
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <select value={filters.client} onChange={e => setFilters({ ...filters, client: e.target.value })} data-testid="filter-client"
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white max-w-[200px]">
            <option value="">Tutti i clienti</option>
            {clients.map(c => <option key={c.email} value={c.email}>{c.company || c.name}</option>)}
          </select>

          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Cerca tracking, cliente..."
              className="pl-8 pr-3 py-2 border border-zinc-200 rounded-sm text-sm w-48" data-testid="filter-search" />
          </div>

          <input type="date" value={filters.dateFrom} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })}
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="filter-date-from" />
          <span className="text-zinc-400 text-xs">—</span>
          <input type="date" value={filters.dateTo} onChange={e => setFilters({ ...filters, dateTo: e.target.value })}
            className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="filter-date-to" />
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex items-center gap-4">
        <button onClick={selectAll} data-testid="select-all-btn"
          className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-sm text-sm font-bold uppercase text-zinc-600 hover:bg-zinc-50">
          {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare size={16} /> : <Square size={16} />}
          {selected.size === filtered.length && filtered.length > 0 ? "Deseleziona Tutto" : "Seleziona Tutto"}
        </button>

        <span className="text-sm text-zinc-500">{filtered.length} risultati | {totalLabels} etichette totali</span>

        <div className="flex-1" />

        {selected.size > 0 && (
          <button onClick={printSelected} data-testid="print-selected-btn"
            className="flex items-center gap-2 bg-[#FF5200] text-white px-5 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-[#e64a00] transition-colors">
            <Printer size={16} /> Stampa {selected.size} ({selectedLabels} etichette)
          </button>
        )}
      </div>

      {/* Items list */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="labels-table">
          <thead><tr className="bg-[#09090B] text-white text-xs uppercase tracking-wider">
            <th className="px-3 py-3 w-10"></th>
            <th className="px-3 py-3 text-left">Tipo</th>
            <th className="px-3 py-3 text-left">Tracking</th>
            <th className="px-3 py-3 text-left">Cliente</th>
            <th className="px-3 py-3 text-left">Tratta</th>
            <th className="px-3 py-3 text-center">Colli</th>
            <th className="px-3 py-3 text-left">Stato</th>
            <th className="px-3 py-3 text-left">Data</th>
            <th className="px-3 py-3"></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-8 text-zinc-400">Nessun risultato con i filtri selezionati</td></tr>
            ) : filtered.map((item, i) => {
              const colli = item.colli && item.colli.length > 0 ? item.colli : null;
              const numLabels = colli ? colli.reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) : 1;
              const isSelected = selected.has(item.id);
              const statusColors = {
                in_attesa: "bg-amber-100 text-amber-700", accettato: "bg-blue-100 text-blue-700",
                in_transito: "bg-violet-100 text-violet-700", consegnato: "bg-green-100 text-green-700",
                cancellation_requested: "bg-red-100 text-red-600",
              };
              return (
                <tr key={item.id || i} className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${isSelected ? "bg-orange-50" : ""}`}
                  onClick={() => toggleSelect(item.id)} data-testid={`label-row-${i}`}>
                  <td className="px-3 py-2.5 text-center">
                    {isSelected ? <CheckSquare size={18} className="text-[#FF5200]" /> : <Square size={18} className="text-zinc-300" />}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${item._type === "spedizione" ? "bg-blue-50 text-blue-600" : "bg-violet-50 text-violet-600"}`}>
                      {item._type === "spedizione" ? <Package size={12} /> : <Truck size={12} />}
                      {item._type === "spedizione" ? "SP" : "RT"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono font-bold">{item.tracking_number}</td>
                  <td className="px-3 py-2.5 text-zinc-600">{item.company || item.client_name || "-"}</td>
                  <td className="px-3 py-2.5 text-zinc-600 text-xs">{item.origin} → {item.destination}</td>
                  <td className="px-3 py-2.5 text-center font-bold">{numLabels}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm ${statusColors[item.status] || "bg-zinc-100 text-zinc-500"}`}>
                      {item.status?.replace(/_/g, " ") || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs">{formatDate(item.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={(e) => { e.stopPropagation(); printShipmentLabel(item, { kind: item._type === "ritiro" ? "pickup" : "shipment", useServer: true }); }} data-testid={`print-single-${i}`}
                      className="p-1.5 text-zinc-400 hover:text-[#FF5200] transition-colors" title="Stampa etichetta">
                      <Printer size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
